import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  type AngleMode,
  evaluate,
  formatNumber,
  generateTable,
  solveQuadratic,
  solveCubic,
  solveLin2,
  solveLin3,
  toFractionTex,
} from "@/lib/calculators/casio-fx991es";

type Bilingual = { en: string; bn: string };
type Mode = "COMP" | "STAT" | "TABLE" | "EQN";
type EqnKind = "lin2" | "lin3" | "quad" | "cubic";

const STR = {
  h1: { en: "Casio FX-991ES Plus Emulator", bn: "ক্যাসিও FX-991ES Plus এমুলেটর" },
  desc: {
    en: "Faithful online emulator of the Casio FX-991ES Plus — the standard scientific calculator for SSC, HSC and admission test prep in Bangladesh.",
    bn: "ক্যাসিও FX-991ES Plus এর অনলাইন এমুলেটর — SSC, HSC ও ভর্তি পরীক্ষার প্রস্তুতির আদর্শ ক্যালকুলেটর।",
  },
  examNote: {
    en: "This emulator is for practice only. A physical Casio is required in actual exams.",
    bn: "এই এমুলেটরটি শুধু অনুশীলনের জন্য। প্রকৃত পরীক্ষায় ফিজিক্যাল ক্যাসিও ব্যবহার করতে হবে।",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "Is this the same as the Casio FX-991ES Plus?", bn: "এটা কি ক্যাসিও FX-991ES Plus এর মতই?" },
    a: {
      en: "It mirrors the most-used functions and the natural-display behaviour of the real device. Some advanced features (matrix, vector, ratio mode) are simplified or linked to dedicated calculators on this site.",
      bn: "এটি প্রকৃত যন্ত্রের সবচেয়ে ব্যবহৃত ফাংশন ও ন্যাচারাল ডিসপ্লে আচরণ অনুসরণ করে। কিছু উন্নত মোড (ম্যাট্রিক্স, ভেক্টর) সরলীকৃত।",
    },
  },
  {
    q: { en: "Can I use this in an exam?", bn: "পরীক্ষায় কি এটি ব্যবহার করা যাবে?" },
    a: {
      en: "No. Boards and university admission tests in Bangladesh require an actual physical calculator. Use this emulator only for home practice.",
      bn: "না। বাংলাদেশের বোর্ড ও ভর্তি পরীক্ষায় শুধুমাত্র ফিজিক্যাল ক্যালকুলেটর অনুমোদিত।",
    },
  },
  {
    q: { en: "How to switch DEG / RAD?", bn: "DEG / RAD কীভাবে বদলাব?" },
    a: {
      en: "Tap the DEG / RAD / GRAD chip above the display, or press SHIFT + MODE to open the setup menu (just like on the real device).",
      bn: "ডিসপ্লের উপরের DEG / RAD / GRAD চিপে ট্যাপ করুন, অথবা SHIFT + MODE চাপুন।",
    },
  },
  {
    q: { en: "How do I enter π?", bn: "π কীভাবে লিখব?" },
    a: { en: "Press SHIFT then ×10ˣ (the dedicated π key on the real device).", bn: "SHIFT তারপর ×10ˣ চাপুন।" },
  },
  {
    q: { en: "How does fraction display work?", bn: "ভগ্নাংশ ডিসপ্লে কীভাবে কাজ করে?" },
    a: {
      en: "When a result is a small rational number (denominator ≤ 1000), the calculator shows it as a stacked fraction. Press S↔D (or SHIFT + =) to toggle between fraction and decimal.",
      bn: "ফলাফল ছোট ভগ্নাংশ হলে এটি স্ট্যাকড আকারে দেখায়। ভগ্নাংশ ↔ দশমিক বদলাতে S↔D চাপুন।",
    },
  },
  {
    q: { en: "Why is sin(30) showing a weird number?", bn: "sin(30) অদ্ভুত মান দেখাচ্ছে কেন?" },
    a: {
      en: "Make sure the angle mode is DEG. In RAD, sin(30) ≈ −0.988. Switch to DEG and sin(30) = 0.5.",
      bn: "অ্যাঙ্গেল মোড DEG কিনা দেখুন। RAD এ sin(30) ≈ −0.988। DEG এ sin(30) = 0.5।",
    },
  },
  {
    q: { en: "ক্যাসিও ক্যালকুলেটর অনলাইনে কীভাবে ব্যবহার করব?", bn: "ক্যাসিও ক্যালকুলেটর অনলাইনে কীভাবে ব্যবহার করব?" },
    a: {
      en: "প্রথমে MODE চেপে COMP মোডে যান। এরপর সাধারণ ক্যালকুলেটরের মতই অভিব্যক্তি লিখে = চাপুন। কীবোর্ডের সংখ্যা ও অপারেটর কী কাজ করে।",
      bn: "প্রথমে MODE চেপে COMP মোডে যান। এরপর সাধারণ ক্যালকুলেটরের মতই অভিব্যক্তি লিখে = চাপুন। কীবোর্ডের সংখ্যা ও অপারেটর কী কাজ করে।",
    },
  },
  {
    q: { en: "Difference between FX-991ES Plus and FX-991EX?", bn: "FX-991ES Plus আর FX-991EX এর পার্থক্য?" },
    a: {
      en: "The FX-991EX (ClassWiz) is the newer model with a higher-resolution display, faster CPU, QR code output and Spreadsheet mode. The FX-991ES Plus is older but still the most common in Bangladesh classrooms.",
      bn: "FX-991EX (ClassWiz) নতুন মডেল — উন্নত ডিসপ্লে, দ্রুত CPU ও স্প্রেডশিট মোড। FX-991ES Plus পুরোনো হলেও বাংলাদেশে সবচেয়ে প্রচলিত।",
    },
  },
];

export const Route = createFileRoute("/casio-fx-991es")({
  head: () => {
    const seo = buildSeo({
      title: "Casio FX-991ES Plus Emulator Online — Free",
      description:
        "Free online Casio FX-991ES Plus emulator. Faithful replica for SSC, HSC, admission test prep. Practice the calculator you'll use in exams. ক্যাসিও ক্যালকুলেটর।",
      path: "/casio-fx-991es",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Casio FX-991ES Plus", path: "/casio-fx-991es" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CasioFx991esPage,
});

// ---------------------------------------------------------------------------
function CasioFx991esPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];

  return (
    <CalculatorLayout
      slug="casio-fx-991es"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Calculator"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className={cn("mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-300", lang === "bn" && "font-bn")}>
        ⚠ {tr("examNote")}
      </div>

      <CasioDevice />

      <EducationalContent />
    </CalculatorLayout>
  );
}

// ===========================================================================
// Device shell
// ===========================================================================
function CasioDevice() {
  const [mode, setMode] = useState<Mode>("COMP");
  const [angle, setAngle] = useState<AngleMode>("DEG");
  const [shift, setShift] = useState(false);
  const [alpha, setAlpha] = useState(false);
  const [showModeMenu, setShowModeMenu] = useState(false);

  // COMP-mode calculator state
  const [buffer, setBuffer] = useState("");
  const [cursor, setCursor] = useState(0);
  const [resultDisplay, setResultDisplay] = useState<string>("0");
  const [resultTex, setResultTex] = useState<string | null>(null);
  const [ans, setAns] = useState(0);
  const [history, setHistory] = useState<{ buf: string; res: string }[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [memory, setMemory] = useState(0);
  const [showFraction, setShowFraction] = useState(true);

  const onMode = (m: Mode) => { setMode(m); setShowModeMenu(false); reset(); };

  const reset = useCallback(() => {
    setBuffer(""); setCursor(0); setResultDisplay("0"); setResultTex(null); setError(null); setHistoryIdx(-1);
  }, []);

  const insert = useCallback((token: string) => {
    setError(null);
    setBuffer((b) => b.slice(0, cursor) + token + b.slice(cursor));
    setCursor((c) => c + token.length);
  }, [cursor]);

  const equals = useCallback(() => {
    if (!buffer.trim()) return;
    const r = evaluate(buffer, { angle, ans, vars: { A: 0, B: 0, C: 0, D: 0, X: 0, Y: 0, M: memory } });
    if (!r.ok) {
      setError(r.kind);
      setResultDisplay(r.kind);
      setResultTex(null);
      return;
    }
    setAns(r.value);
    setResultDisplay(r.display);
    setResultTex(showFraction ? r.fractionTex ?? null : null);
    setError(null);
    setHistory((h) => [{ buf: buffer, res: r.display }, ...h].slice(0, 6));
    setHistoryIdx(-1);
  }, [buffer, angle, ans, memory, showFraction]);

  const toggleSD = () => {
    if (resultTex) { setResultTex(null); }
    else {
      const tex = toFractionTex(ans);
      if (tex) setResultTex(tex);
    }
  };

  const replayUp = () => {
    if (history.length === 0) return;
    const next = Math.min(history.length - 1, historyIdx + 1);
    setHistoryIdx(next);
    setBuffer(history[next].buf);
    setCursor(history[next].buf.length);
  };
  const replayDown = () => {
    if (historyIdx <= 0) { setHistoryIdx(-1); return; }
    const next = historyIdx - 1;
    setHistoryIdx(next);
    setBuffer(history[next].buf);
    setCursor(history[next].buf.length);
  };

  const del = () => {
    if (cursor === 0) return;
    setBuffer((b) => b.slice(0, cursor - 1) + b.slice(cursor));
    setCursor((c) => c - 1);
  };

  // Resolve token from a button press
  const press = useCallback((label: string) => {
    if (label === "SHIFT") { setShift((s) => !s); setAlpha(false); return; }
    if (label === "ALPHA") { setAlpha((a) => !a); setShift(false); return; }
    if (label === "MODE") { setShowModeMenu(true); setShift(false); setAlpha(false); return; }

    let tok = label;
    if (shift) {
      const map: Record<string, string> = {
        sin: "asin(", cos: "acos(", tan: "atan(",
        log: "10^(", ln: "e^(",
        "√": "³√(", "(-)": "π", ".": "Random()",
        ")": "%", "7": "nPr(", "8": "nCr(",
        "×10ˣ": "π", "AC": "OFF", "=": "S↔D",
      };
      if (map[label]) tok = map[label];
      setShift(false);
    }
    // Translate display tokens into buffer tokens
    const display: Record<string, string> = {
      "×": "×", "÷": "÷", "+": "+", "-": "−", "(-)": "−",
      "sin": "sin(", "cos": "cos(", "tan": "tan(",
      "asin(": "asin(", "acos(": "acos(", "atan(": "atan(",
      "log": "log(", "ln": "ln(",
      "√": "√(", "³√(": "³√(",
      "□²": "^2", "□^□": "^(",
      "π": "π", "Ans": "Ans",
      "×10ˣ": "×10^",
      "10^(": "10^(", "e^(": "e^(",
      "%": "%", "nPr(": "P(", "nCr(": "C(",
      "Random()": String(Math.random().toFixed(3)),
      "a/b": "/",
      "(": "(", ")": ")",
    };
    const text = display[tok] ?? tok;

    if (tok === "OFF") { reset(); setResultDisplay("Goodbye"); return; }
    if (tok === "S↔D") { toggleSD(); return; }
    if (tok === "AC") { reset(); return; }
    if (tok === "DEL") { del(); return; }
    if (tok === "=") { equals(); return; }
    if (tok === "▲") { replayUp(); return; }
    if (tok === "▼") { replayDown(); return; }
    if (tok === "◀") { setCursor((c) => Math.max(0, c - 1)); return; }
    if (tok === "▶") { setCursor((c) => Math.min(buffer.length, c + 1)); return; }
    if (tok === "M+") { setMemory((m) => m + ans); return; }
    if (tok === "RCL") { insert(`${memory}`); return; }
    if (tok === "STO") { setMemory(ans); return; }

    insert(text);
  }, [shift, alpha, equals, insert, del, replayUp, replayDown, ans, memory, buffer.length, reset]);

  // Keyboard input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const k = e.key;
      if (mode !== "COMP") return;
      if (/^[0-9]$/.test(k)) { press(k); e.preventDefault(); return; }
      const mapping: Record<string, string> = {
        "+": "+", "-": "-", "*": "×", "/": "÷", ".": ".",
        "(": "(", ")": ")", "^": "□^□",
        "Enter": "=", "=": "=",
        "Backspace": "DEL", "Escape": "AC",
        "ArrowUp": "▲", "ArrowDown": "▼", "ArrowLeft": "◀", "ArrowRight": "▶",
        "s": "sin", "c": "cos", "t": "tan",
        "l": "log", "L": "ln", "p": "π",
        "r": "√", "S": "SHIFT", "A": "ALPHA",
      };
      if (mapping[k]) { press(mapping[k]); e.preventDefault(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [press, mode]);

  return (
    <div className="overflow-hidden rounded-3xl bg-gradient-to-b from-zinc-700 via-zinc-800 to-zinc-900 p-3 shadow-2xl sm:p-5">
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-300">
        <span className="font-semibold">CASIO</span>
        <span className="rounded bg-zinc-600 px-1.5 py-0.5 text-[9px] font-bold text-zinc-100">fx-991ES PLUS</span>
        <span className="text-[9px]">NATURAL DISPLAY</span>
      </div>

      <Display
        buffer={buffer}
        cursor={cursor}
        result={resultDisplay}
        resultTex={resultTex}
        shift={shift}
        alpha={alpha}
        angle={angle}
        memory={memory !== 0}
        error={error}
      />

      <div className="mt-2 flex flex-wrap gap-1.5 px-1 text-[10px]">
        {(["DEG", "RAD", "GRAD"] as AngleMode[]).map((a) => (
          <button
            key={a}
            onClick={() => setAngle(a)}
            className={cn(
              "rounded px-2 py-0.5 font-mono",
              angle === a ? "bg-amber-400 text-zinc-900" : "bg-zinc-600 text-zinc-200 hover:bg-zinc-500",
            )}
          >{a}</button>
        ))}
        <span className="ml-auto text-[10px] text-zinc-400">
          mode: <span className="font-mono text-zinc-200">{mode}</span>
        </span>
      </div>

      {showModeMenu && (
        <ModeMenu current={mode} onPick={onMode} onClose={() => setShowModeMenu(false)} />
      )}

      {mode === "COMP" && <KeyPad press={press} shift={shift} alpha={alpha} />}
      {mode === "EQN" && <EqnPanel onExit={() => setMode("COMP")} />}
      {mode === "TABLE" && <TablePanel angle={angle} onExit={() => setMode("COMP")} />}
      {mode === "STAT" && <StatPanel onExit={() => setMode("COMP")} />}

      <div className="mt-3 flex items-center justify-between px-1 text-[10px] text-zinc-400">
        <button onClick={reset} className="rounded bg-zinc-700 px-2 py-1 text-zinc-200 hover:bg-zinc-600">Reset</button>
        <span>Ans = <span className="font-mono text-zinc-200">{formatNumber(ans)}</span></span>
      </div>
    </div>
  );
}

// ===========================================================================
function Display({
  buffer, cursor, result, resultTex, shift, alpha, angle, memory, error,
}: {
  buffer: string; cursor: number; result: string; resultTex: string | null;
  shift: boolean; alpha: boolean; angle: AngleMode; memory: boolean; error: string | null;
}) {
  const before = buffer.slice(0, cursor);
  const after = buffer.slice(cursor);
  return (
    <div className="rounded-md border-2 border-zinc-900 bg-[#cfe3a8] p-2 font-mono text-zinc-900 shadow-inner">
      <div className="flex items-center gap-2 text-[9px] font-bold uppercase">
        <span className={shift ? "" : "opacity-30"}>S</span>
        <span className={alpha ? "" : "opacity-30"}>A</span>
        <span className="ml-auto">{angle === "DEG" ? "D" : angle === "RAD" ? "R" : "G"}</span>
        <span className={memory ? "" : "opacity-30"}>M</span>
      </div>
      <div className="min-h-[1.4em] overflow-x-auto whitespace-nowrap text-sm">
        {error ? (
          <span className="text-red-700">{error}</span>
        ) : (
          <>
            <span>{before}</span>
            <span className="inline-block w-px animate-pulse bg-zinc-900" style={{ height: "1em", verticalAlign: "middle" }}>&nbsp;</span>
            <span>{after}</span>
            {!buffer && <span className="opacity-30">0</span>}
          </>
        )}
      </div>
      <div className="mt-1 min-h-[1.6em] text-right text-2xl font-bold">
        {resultTex ? (
          <span className="inline-block"><InlineMath math={resultTex} /></span>
        ) : (
          <span>{result}</span>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
function ModeMenu({ current, onPick, onClose }: { current: Mode; onPick: (m: Mode) => void; onClose: () => void }) {
  const items: { v: Mode; label: string }[] = [
    { v: "COMP", label: "1: COMP" },
    { v: "STAT", label: "2: STAT" },
    { v: "TABLE", label: "3: TABLE" },
    { v: "EQN", label: "4: EQN" },
  ];
  return (
    <div className="mt-2 rounded-md border border-zinc-600 bg-zinc-800 p-2 text-xs text-zinc-100">
      <div className="mb-1 flex items-center justify-between">
        <span className="font-semibold">Select Mode</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100">×</button>
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {items.map((i) => (
          <button key={i.v} onClick={() => onPick(i.v)} className={cn(
            "rounded px-2 py-1.5 text-left font-mono",
            current === i.v ? "bg-amber-400 text-zinc-900" : "bg-zinc-700 hover:bg-zinc-600",
          )}>{i.label}</button>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-zinc-400">
        Modes 5–8 (Matrix/Vector/Inequality/Ratio):{" "}
        <Link to="/scientific-calculator" className="text-amber-400 hover:underline">use Scientific Calculator</Link>
      </div>
    </div>
  );
}

// ===========================================================================
// COMP keypad
// ===========================================================================
type KeyDef = { label: string; sub?: string; cls: string; wide?: boolean };
const KEYS: KeyDef[][] = [
  [
    { label: "SHIFT", sub: "", cls: "bg-amber-500 text-zinc-900" },
    { label: "ALPHA", sub: "", cls: "bg-red-600 text-white" },
    { label: "▲", sub: "", cls: "bg-blue-700 text-white" },
    { label: "MODE", sub: "SETUP", cls: "bg-zinc-700 text-white" },
    { label: "ON", sub: "OFF", cls: "bg-red-600 text-white" },
  ],
  [
    { label: "Abs", sub: "|x|", cls: "bg-zinc-700 text-white" },
    { label: "a/b", sub: "÷R", cls: "bg-zinc-700 text-white" },
    { label: "√", sub: "³√", cls: "bg-zinc-700 text-white" },
    { label: "□²", sub: "x³", cls: "bg-zinc-700 text-white" },
    { label: "□^□", sub: "x⁻¹", cls: "bg-zinc-700 text-white" },
  ],
  [
    { label: "log", sub: "10ˣ", cls: "bg-zinc-700 text-white" },
    { label: "ln", sub: "eˣ", cls: "bg-zinc-700 text-white" },
    { label: "(-)", sub: "π", cls: "bg-zinc-700 text-white" },
    { label: "°'\"", sub: "DRG", cls: "bg-zinc-700 text-white" },
    { label: "hyp", sub: "", cls: "bg-zinc-700 text-white" },
  ],
  [
    { label: "sin", sub: "sin⁻¹", cls: "bg-zinc-700 text-white" },
    { label: "cos", sub: "cos⁻¹", cls: "bg-zinc-700 text-white" },
    { label: "tan", sub: "tan⁻¹", cls: "bg-zinc-700 text-white" },
    { label: "RCL", sub: "STO", cls: "bg-zinc-700 text-white" },
    { label: "ENG", sub: "←", cls: "bg-zinc-700 text-white" },
  ],
  [
    { label: "(", sub: "%", cls: "bg-zinc-700 text-white" },
    { label: ")", sub: "%", cls: "bg-zinc-700 text-white" },
    { label: ",", sub: ";", cls: "bg-zinc-700 text-white" },
    { label: "STO", sub: "", cls: "bg-zinc-700 text-white" },
    { label: "M+", sub: "M-", cls: "bg-zinc-700 text-white" },
  ],
  [
    { label: "7", sub: "nPr", cls: "bg-zinc-900 text-white" },
    { label: "8", sub: "nCr", cls: "bg-zinc-900 text-white" },
    { label: "9", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "DEL", sub: "INS", cls: "bg-red-600 text-white" },
    { label: "AC", sub: "OFF", cls: "bg-red-600 text-white" },
  ],
  [
    { label: "4", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "5", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "6", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "×", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "÷", sub: "", cls: "bg-zinc-900 text-white" },
  ],
  [
    { label: "1", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "2", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "3", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "+", sub: "", cls: "bg-zinc-900 text-white" },
    { label: "-", sub: "", cls: "bg-zinc-900 text-white" },
  ],
  [
    { label: "0", sub: "", cls: "bg-zinc-900 text-white" },
    { label: ".", sub: "RND", cls: "bg-zinc-900 text-white" },
    { label: "×10ˣ", sub: "π", cls: "bg-zinc-900 text-white" },
    { label: "Ans", sub: "DRG", cls: "bg-zinc-900 text-white" },
    { label: "=", sub: "S↔D", cls: "bg-amber-500 text-zinc-900" },
  ],
];

function KeyPad({ press, shift, alpha }: { press: (l: string) => void; shift: boolean; alpha: boolean }) {
  // navigation pad row: ◀ ▼ ▶
  return (
    <div className="mt-3 space-y-1.5">
      {/* Replay/cursor pad */}
      <div className="grid grid-cols-5 gap-1.5">
        <Btn def={KEYS[0][0]} press={press} active={shift} />
        <Btn def={KEYS[0][1]} press={press} active={alpha} />
        <Btn def={KEYS[0][2]} press={press} />
        <Btn def={KEYS[0][3]} press={press} />
        <Btn def={KEYS[0][4]} press={press} />
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        <div />
        <Btn def={{ label: "◀", sub: "", cls: "bg-blue-700 text-white" }} press={press} />
        <Btn def={{ label: "▼", sub: "", cls: "bg-blue-700 text-white" }} press={press} />
        <Btn def={{ label: "▶", sub: "", cls: "bg-blue-700 text-white" }} press={press} />
        <div />
      </div>
      {KEYS.slice(1).map((row, i) => (
        <div key={i} className="grid grid-cols-5 gap-1.5">
          {row.map((k) => <Btn key={k.label} def={k} press={press} />)}
        </div>
      ))}
    </div>
  );
}

function Btn({ def, press, active }: { def: KeyDef; press: (l: string) => void; active?: boolean }) {
  return (
    <motion.button
      whileTap={{ scale: 0.94, y: 1 }}
      onClick={() => press(def.label)}
      className={cn(
        "relative h-11 rounded-md text-xs font-bold shadow-md sm:h-12 sm:text-sm",
        def.cls,
        active && "ring-2 ring-amber-300 ring-offset-1 ring-offset-zinc-800",
      )}
    >
      {def.sub && <span className="absolute left-1 top-0 text-[8px] font-medium text-amber-300">{def.sub}</span>}
      <span>{def.label}</span>
    </motion.button>
  );
}

// ===========================================================================
// EQN sub-mode
// ===========================================================================
function EqnPanel({ onExit }: { onExit: () => void }) {
  const [kind, setKind] = useState<EqnKind>("quad");
  const [coefs, setCoefs] = useState<Record<string, string>>({});

  const num = (k: string) => Number(coefs[k] ?? "0") || 0;

  const result = useMemo(() => {
    if (kind === "quad") return solveQuadratic(num("a"), num("b"), num("c"));
    if (kind === "cubic") return solveCubic(num("a"), num("b"), num("c"), num("d"));
    if (kind === "lin2") return solveLin2(num("a1"), num("b1"), num("c1"), num("a2"), num("b2"), num("c2"));
    return solveLin3([
      [num("a1"), num("b1"), num("c1"), num("d1")],
      [num("a2"), num("b2"), num("c2"), num("d2")],
      [num("a3"), num("b3"), num("c3"), num("d3")],
    ]);
  }, [kind, coefs]);

  const fields: Record<EqnKind, string[]> = {
    quad: ["a", "b", "c"],
    cubic: ["a", "b", "c", "d"],
    lin2: ["a1", "b1", "c1", "a2", "b2", "c2"],
    lin3: ["a1", "b1", "c1", "d1", "a2", "b2", "c2", "d2", "a3", "b3", "c3", "d3"],
  };

  return (
    <div className="mt-3 rounded-md border border-zinc-600 bg-zinc-800 p-3 text-xs text-zinc-100">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">EQN — Equation Solver</span>
        <button onClick={onExit} className="rounded bg-zinc-700 px-2 py-0.5 hover:bg-zinc-600">← COMP</button>
      </div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {([
          { v: "lin2", label: "aₙx + bₙy = cₙ (2 vars)" },
          { v: "lin3", label: "3 unknowns" },
          { v: "quad", label: "ax² + bx + c = 0" },
          { v: "cubic", label: "ax³ + bx² + cx + d = 0" },
        ] as { v: EqnKind; label: string }[]).map((k) => (
          <button key={k.v} onClick={() => { setKind(k.v); setCoefs({}); }}
            className={cn("rounded px-2 py-1", kind === k.v ? "bg-amber-400 text-zinc-900" : "bg-zinc-700 hover:bg-zinc-600")}>
            {k.label}
          </button>
        ))}
      </div>
      <div className={cn("grid gap-2", kind === "lin3" ? "grid-cols-4" : kind === "lin2" ? "grid-cols-3" : "grid-cols-4")}>
        {fields[kind].map((f) => (
          <label key={f} className="flex flex-col gap-0.5">
            <span className="font-mono text-[10px] text-zinc-400">{f}</span>
            <input
              type="number"
              value={coefs[f] ?? ""}
              onChange={(e) => setCoefs((c) => ({ ...c, [f]: e.target.value }))}
              className="h-8 rounded bg-zinc-700 px-2 font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-amber-400"
            />
          </label>
        ))}
      </div>
      <div className="mt-3 rounded border border-zinc-700 bg-zinc-900 p-2">
        {result.ok ? (
          <ul className="space-y-1">
            {result.pretty.map((p, i) => (
              <li key={i} className="font-mono"><InlineMath math={p} /></li>
            ))}
          </ul>
        ) : (
          <p className="text-red-400">{result.message}</p>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// TABLE sub-mode
// ===========================================================================
function TablePanel({ angle, onExit }: { angle: AngleMode; onExit: () => void }) {
  const [expr, setExpr] = useState("X^2");
  const [start, setStart] = useState("0");
  const [end, setEnd] = useState("4");
  const [step, setStep] = useState("1");

  const rows = useMemo(
    () => generateTable(expr, Number(start) || 0, Number(end) || 0, Math.max(1e-6, Number(step) || 1), angle),
    [expr, start, end, step, angle],
  );

  return (
    <div className="mt-3 rounded-md border border-zinc-600 bg-zinc-800 p-3 text-xs text-zinc-100">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">TABLE — f(X) generator</span>
        <button onClick={onExit} className="rounded bg-zinc-700 px-2 py-0.5 hover:bg-zinc-600">← COMP</button>
      </div>
      <label className="mb-2 block">
        <span className="text-[10px] text-zinc-400">f(X) =</span>
        <input value={expr} onChange={(e) => setExpr(e.target.value)}
          className="h-8 w-full rounded bg-zinc-700 px-2 font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-amber-400" />
      </label>
      <div className="mb-2 grid grid-cols-3 gap-2">
        <Field label="Start" value={start} onChange={setStart} />
        <Field label="End" value={end} onChange={setEnd} />
        <Field label="Step" value={step} onChange={setStep} />
      </div>
      <div className="max-h-56 overflow-auto rounded border border-zinc-700 bg-zinc-900">
        <table className="w-full font-mono">
          <thead className="sticky top-0 bg-zinc-800 text-[10px] text-zinc-400">
            <tr><th className="px-2 py-1 text-left">X</th><th className="px-2 py-1 text-left">f(X)</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-zinc-800">
                <td className="px-2 py-1">{r.x}</td>
                <td className="px-2 py-1">{r.y === null ? "ERROR" : formatNumber(r.y)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (s: string) => void }) {
  return (
    <label className="block">
      <span className="text-[10px] text-zinc-400">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} type="number"
        className="h-8 w-full rounded bg-zinc-700 px-2 font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-amber-400" />
    </label>
  );
}

// ===========================================================================
// STAT mode (basic — mean, sum, sd of a list)
// ===========================================================================
function StatPanel({ onExit }: { onExit: () => void }) {
  const [data, setData] = useState("1, 2, 3, 4, 5");
  const stats = useMemo(() => {
    const xs = data.split(/[\s,]+/).map(Number).filter((n) => Number.isFinite(n));
    if (xs.length === 0) return null;
    const sum = xs.reduce((a, b) => a + b, 0);
    const mean = sum / xs.length;
    const variance = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / xs.length;
    const sd = Math.sqrt(variance);
    return { n: xs.length, sum, mean, sd, min: Math.min(...xs), max: Math.max(...xs) };
  }, [data]);
  return (
    <div className="mt-3 rounded-md border border-zinc-600 bg-zinc-800 p-3 text-xs text-zinc-100">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">STAT — 1-Variable</span>
        <button onClick={onExit} className="rounded bg-zinc-700 px-2 py-0.5 hover:bg-zinc-600">← COMP</button>
      </div>
      <textarea value={data} onChange={(e) => setData(e.target.value)} rows={3}
        className="w-full rounded bg-zinc-700 p-2 font-mono text-zinc-100 outline-none focus:ring-2 focus:ring-amber-400"
        placeholder="Comma-separated numbers" />
      {stats && (
        <dl className="mt-2 grid grid-cols-3 gap-2 font-mono">
          <Stat label="n" value={stats.n} />
          <Stat label="Σx" value={stats.sum} />
          <Stat label="x̄" value={stats.mean} />
          <Stat label="σx" value={stats.sd} />
          <Stat label="min" value={stats.min} />
          <Stat label="max" value={stats.max} />
        </dl>
      )}
    </div>
  );
}
function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-zinc-700 bg-zinc-900 p-2">
      <div className="text-[10px] text-zinc-400">{label}</div>
      <div className="text-zinc-100">{formatNumber(value)}</div>
    </div>
  );
}

// ===========================================================================
// Educational content
// ===========================================================================
function EducationalContent() {
  const lang = useAppStore((s) => s.lang);
  const en = lang === "en";
  return (
    <div className="mt-10 space-y-8">
      <Section title={en ? "About the Casio FX-991ES Plus" : "ক্যাসিও FX-991ES Plus সম্পর্কে"}>
        <p>{en
          ? "The Casio FX-991ES Plus is a non-programmable scientific calculator with Natural Display — fractions, roots and exponents are written exactly as they appear in a textbook. It supports 417 functions across COMP, STAT, TABLE, EQN, MATRIX, VECTOR, INEQ and RATIO modes."
          : "ক্যাসিও FX-991ES Plus একটি নন-প্রোগ্রামেবল সাইন্টিফিক ক্যালকুলেটর যাতে ন্যাচারাল ডিসপ্লে রয়েছে — ভগ্নাংশ, মূল ও ঘাত পাঠ্যবইয়ের মতই দেখা যায়। এতে ৪১৭টি ফাংশন আছে।"}</p>
      </Section>
      <Section title={en ? "Why this is the standard BD exam calculator" : "BD পরীক্ষার আদর্শ ক্যালকুলেটর কেন"}>
        <p>{en
          ? "Bangladeshi education boards and most universities (DU, BUET, RUET, CUET, MBBS admission) approve a fixed list of non-programmable calculators — the FX-991ES Plus is the most widely accepted. Its layout is muscle memory for SSC and HSC students."
          : "বাংলাদেশের শিক্ষা বোর্ড ও অধিকাংশ বিশ্ববিদ্যালয় (DU, BUET, RUET, CUET, মেডিক্যাল ভর্তি) নন-প্রোগ্রামেবল ক্যালকুলেটরের নির্দিষ্ট তালিকা অনুমোদন করে — FX-991ES Plus সবচেয়ে প্রচলিত।"}</p>
      </Section>
      <Section title={en ? "Key features" : "মূল বৈশিষ্ট্য"}>
        <ul className="ml-5 list-disc space-y-1">
          <li>{en ? "Natural Display (S-V.P.A.M.)" : "ন্যাচারাল ডিসপ্লে"}</li>
          <li>{en ? "417 functions, 40 scientific constants, 40 unit conversions" : "৪১৭ ফাংশন, ৪০ ধ্রুবক, ৪০ একক রূপান্তর"}</li>
          <li>{en ? "Equation solver up to cubic, 3-variable linear systems" : "কিউবিক পর্যন্ত সমীকরণ সমাধান, ৩-চলক রৈখিক সিস্টেম"}</li>
          <li>{en ? "Matrix and vector arithmetic" : "ম্যাট্রিক্স ও ভেক্টর গণিত"}</li>
          <li>{en ? "Statistics with regression analysis" : "রিগ্রেশন সহ পরিসংখ্যান"}</li>
          <li>{en ? "Function table generation" : "ফাংশন টেবিল তৈরি"}</li>
          <li>{en ? "Solar + battery (LR44) hybrid power" : "সৌর + ব্যাটারি (LR44) হাইব্রিড পাওয়ার"}</li>
        </ul>
      </Section>
      <Section title={en ? "Button function reference" : "বোতামের কার্যাবলী"}>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted text-muted-foreground"><tr><th className="px-2 py-1 text-left">Key</th><th className="px-2 py-1 text-left">Primary</th><th className="px-2 py-1 text-left">SHIFT</th></tr></thead>
            <tbody className="font-mono">
              {[
                ["sin", "sine", "sin⁻¹"],
                ["cos", "cosine", "cos⁻¹"],
                ["tan", "tangent", "tan⁻¹"],
                ["log", "log base 10", "10ˣ"],
                ["ln", "natural log", "eˣ"],
                ["√", "square root", "³√"],
                ["x²", "square", "x³"],
                ["(-)", "negative sign", "π"],
                ["×10ˣ", "scientific exp", "π"],
                ["7", "digit 7", "nPr"],
                ["8", "digit 8", "nCr"],
                ["=", "evaluate", "S↔D"],
              ].map((r) => (
                <tr key={r[0]} className="border-t border-border"><td className="px-2 py-1">{r[0]}</td><td className="px-2 py-1">{r[1]}</td><td className="px-2 py-1 text-amber-700 dark:text-amber-400">{r[2]}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title={en ? "Common SSC math problems" : "SSC গণিতের সাধারণ সমস্যা"}>
        <ul className="ml-5 list-disc space-y-1">
          <li>{en ? "Trigonometry: sin 30°, cos 60°, tan 45° — set DEG mode first." : "ত্রিকোণমিতি: sin 30°, cos 60°, tan 45° — প্রথমে DEG মোডে যান।"}</li>
          <li>{en ? "Quadratic roots via EQN mode (x² − 5x + 6 → 2, 3)." : "EQN মোডে দ্বিঘাত সমীকরণের মূল।"}</li>
          <li>{en ? "Surds / square roots: √2, √3 — display stays in surd form." : "করণী রূপ ধরে রাখে।"}</li>
        </ul>
      </Section>
      <Section title={en ? "Common HSC physics / chemistry" : "HSC পদার্থ / রসায়ন"}>
        <ul className="ml-5 list-disc space-y-1">
          <li>{en ? "Logarithms in pH calculations (−log₁₀[H⁺])." : "pH হিসাবে লগারিদম।"}</li>
          <li>{en ? "Exponential growth/decay with eˣ." : "এক্সপোনেনশিয়াল বৃদ্ধি/ক্ষয়।"}</li>
          <li>{en ? "Vector and matrix operations for kinematics and linear algebra." : "ভেক্টর ও ম্যাট্রিক্স অপারেশন।"}</li>
        </ul>
      </Section>
      <Section title={en ? "Admission test usage (DU, BUET, MBBS)" : "ভর্তি পরীক্ষা (DU, BUET, MBBS)"}>
        <p>{en
          ? "All three institutions allow the FX-991ES Plus on their approved list. Practice common operations (statistics, complex evaluation, EQN) with this emulator before exam day."
          : "DU, BUET ও MBBS ভর্তি পরীক্ষায় FX-991ES Plus অনুমোদিত। পরীক্ষার আগে এই এমুলেটরে অনুশীলন করুন।"}</p>
      </Section>
      <Section title={en ? "Tips & tricks" : "টিপস ও কৌশল"}>
        <ul className="ml-5 list-disc space-y-1">
          <li>{en ? "▲ recalls previous expression for editing — saves time on multi-step problems." : "▲ আগের অভিব্যক্তি ফিরিয়ে আনে — সম্পাদনা ও পুনরায় হিসাব করুন।"}</li>
          <li>{en ? "S↔D toggles between fraction and decimal display." : "S↔D ভগ্নাংশ ও দশমিকের মধ্যে বদলায়।"}</li>
          <li>{en ? "STO + variable saves the result; recall with ALPHA + variable." : "STO ফলাফল সংরক্ষণ করে; ALPHA + চলকে কল করুন।"}</li>
          <li>{en ? "M+ adds Ans into independent memory M." : "M+ Ans কে M মেমরিতে যোগ করে।"}</li>
        </ul>
      </Section>
      <Section title={en ? "Mode usage" : "মোড ব্যবহার"}>
        <ul className="ml-5 list-disc space-y-1">
          <li><b>COMP</b> — {en ? "general computation, default mode." : "সাধারণ হিসাব, ডিফল্ট মোড।"}</li>
          <li><b>STAT</b> — {en ? "1-variable and 2-variable statistics with regression." : "১ ও ২ চলক পরিসংখ্যান।"}</li>
          <li><b>TABLE</b> — {en ? "tabulate f(x) over a range." : "ফাংশনের মান ছকে দেখান।"}</li>
          <li><b>EQN</b> — {en ? "linear (2/3 vars), quadratic and cubic solvers." : "রৈখিক, দ্বিঘাত ও ত্রিঘাত সমাধান।"}</li>
        </ul>
      </Section>
      <Section title={en ? "FX-991ES Plus vs FX-991EX" : "FX-991ES Plus বনাম FX-991EX"}>
        <p>{en
          ? "The FX-991EX (ClassWiz) has a 192×63 dot display (vs 96×31), runs about 4× faster, supports QR-code output and Spreadsheet mode. The FX-991ES Plus remains the most widely owned model in BD."
          : "FX-991EX এ উন্নত ডিসপ্লে, দ্রুত CPU, QR কোড আউটপুট ও স্প্রেডশিট মোড আছে। তবে FX-991ES Plus এখনও বাংলাদেশে সবচেয়ে জনপ্রিয়।"}{" "}
          <Link to="/casio-fx-991ex" className="text-accent hover:underline">{en ? "Open FX-991EX emulator →" : "FX-991EX এমুলেটর →"}</Link></p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const lang = useAppStore((s) => s.lang);
  return (
    <section className={cn(lang === "bn" && "font-bn")}>
      <h2 className="mb-2 text-lg font-bold">{title}</h2>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

// suppress unused var lint
void useRef;
